/**
 * @file src/components/organisms/CoinbaseOnRamp/CoinbaseOnRampButton.tsx
 * @description Defines a button component for initiating Coinbase On-Ramp using a portal.
 * @module CoinbaseOnRampButton
 */

import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components';
import { initOnRamp, CBPayInstanceType } from '@coinbase/cbpay-js';
import APP_CONFIG from '@/config/app';
import { logInfo } from '@/utils/logger';
import { useWalletsContext } from '@/components/providers';

interface CoinbaseOnRampButtonProps {
  className?: string;
}

const CoinbaseOnRampButton: FC<CoinbaseOnRampButtonProps> = ({ className }) => {
  const [onrampInstance, setOnrampInstance] =
    useState<CBPayInstanceType | null>(null);
  const [widgetContainer, setWidgetContainer] = useState<HTMLElement | null>(
    null,
  );

  const { wallets } = useWalletsContext();

  const addresses = useMemo(() => {
    return wallets.reduce((acc, wallet) => {
      const blockchain =
        wallet.blockchain === 'ethereum'
          ? 'ethereum'
          : wallet.blockchain === 'solana'
          ? 'solana'
          : null;
      if (blockchain) {
        acc[wallet.address] = [blockchain];
      }
      return acc;
    }, {} as { [key: string]: string[] });
  }, [wallets]);

  useEffect(() => {
    if (!onrampInstance) {
      initOnRamp(
        {
          appId: APP_CONFIG.COINBASE_APP_ID,
          widgetParameters: {
            addresses,
          },
          onSuccess: () => {
            logInfo(
              'Coinbase On-Ramp successful',
              'CoinbaseOnRampButton/onSuccess',
            );
            if (widgetContainer) {
              document.body.removeChild(widgetContainer);
              setWidgetContainer(null);
            }
          },
          onExit: () => {
            logInfo('Coinbase On-Ramp exited', 'CoinbaseOnRampButton/onExit');
            if (widgetContainer) {
              document.body.removeChild(widgetContainer);
              setWidgetContainer(null);
            }
          },
          onEvent: (event) => {
            logInfo(
              'Coinbase On-Ramp event',
              'CoinbaseOnRampButton/onEvent',
              event,
            );
          },
          experienceLoggedIn: 'popup',
          experienceLoggedOut: 'popup',
          closeOnExit: true,
          closeOnSuccess: true,
        },
        (_, instance) => {
          setOnrampInstance(instance);
        },
      );
    }
  }, [onrampInstance, addresses, widgetContainer]);

  const handleOnRamp = useCallback(() => {
    if (!onrampInstance) {
      return;
    }

    let container = widgetContainer;
    if (!container) {
      container = document.createElement('div');
      container.id = 'coinbase-widget';
      document.body.appendChild(container);
      setWidgetContainer(container);
    }
    onrampInstance.open();
  }, [onrampInstance, widgetContainer]);

  return (
    <>
      <Button
        icon="coinbasepaylogolight"
        fullWidth
        className={`${className}`}
        onClick={handleOnRamp}
      />
      {widgetContainer &&
        createPortal(<div id="coinbase-widget"></div>, widgetContainer)}
    </>
  );
};

export default CoinbaseOnRampButton;
